import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Icon } from "./Icon";
import { useUserInputContext } from "../hooks/useUserInputContext";
import { useSessionContext } from "../contexts/useSessionContext";

interface CommentInputProps {
  groupId: string;
  placeholder?: string;
  // Session action type to emit when a note is saved. Omit for the default
  // "commentSave", provide a string to override it, or false to disable tracking.
  action?: string | false;
}

const MAX_CHARACTERS = 280;

export function CommentInput({
  groupId,
  placeholder,
  action,
}: CommentInputProps) {
  const { getComment, setComment } = useUserInputContext();
  const { recordAction } = useSessionContext();
  const existingComment = getComment(groupId);
  const [text, setText] = useState(existingComment);
  const [isFocused, setIsFocused] = useState(false);
  const [isSaved, setIsSaved] = useState(!!existingComment);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync with context on mount
  useEffect(() => {
    setText(existingComment);
    setIsSaved(!!existingComment);
  }, [existingComment]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [text]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    if (newText.length <= MAX_CHARACTERS) {
      setText(newText);
      setIsSaved(false);
    }
  };

  const handleSave = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    // No-op when the saved value is unchanged so whitespace-only edits or
    // repeated saves don't re-store the comment or emit duplicate events.
    if (trimmed === existingComment) {
      setText(trimmed);
      setIsSaved(true);
      textareaRef.current?.blur();
      return;
    }
    setComment(groupId, trimmed);
    setText(trimmed);
    setIsSaved(true);
    textareaRef.current?.blur();
    // Record the saved note by default as "commentSave"; a custom action
    // overrides the name, and action === false disables tracking.
    if (action !== false) {
      recordAction(typeof action === "string" ? action : "commentSave", {
        groupId,
        comment: trimmed,
        ...(placeholder ? { placeholder } : {}),
      });
    }
  };

  const handleClear = () => {
    setText("");
    setComment(groupId, "");
    setIsSaved(false);
    textareaRef.current?.focus();
  };

  const remainingChars = MAX_CHARACTERS - text.length;
  const isNearLimit = remainingChars <= 30;
  const hasContent = text.trim().length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      className="w-full max-w-2xl mx-auto"
    >
      <div
        className={`relative rounded-2xl backdrop-blur-md border transition-all duration-200 ${
          isFocused
            ? "bg-white/15 border-white/40 shadow-lg"
            : isSaved
              ? "bg-emerald-500/10 border-emerald-400/30"
              : "bg-white/10 border-white/20"
        }`}
      >
        {/* Label */}
        <div className="px-4 pt-3 pb-1 flex items-center gap-2">
          <Icon
            icon="comment"
            className={`text-sm ${isSaved ? "text-emerald-400" : "text-white/60"}`}
          />
          <span
            className={`text-xs font-medium ${isSaved ? "text-emerald-400" : "text-white/60"}`}
          >
            {isSaved ? "Your Note" : "Add a Note"}
          </span>
        </div>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(e) => {
            // Stop propagation to prevent navigation handlers from intercepting
            // spacebar, arrows, etc. while typing
            e.stopPropagation();
          }}
          placeholder={placeholder || "Share your thoughts..."}
          rows={2}
          className="w-full px-4 pb-3 pt-1 bg-transparent text-white placeholder-white/40 resize-none focus:outline-none text-base leading-relaxed"
          style={{ minHeight: "60px" }}
        />

        {/* Footer - character count and save button */}
        <div className="px-4 pb-3 flex items-center justify-between">
          <span
            className={`text-xs transition-colors ${
              isNearLimit ? "text-amber-400" : "text-white/40"
            }`}
          >
            {remainingChars}
          </span>

          <div className="flex items-center gap-2">
            {isSaved && hasContent && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={handleClear}
                className="px-3 py-1.5 text-xs rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors cursor-pointer"
              >
                Clear
              </motion.button>
            )}

            <motion.button
              onClick={handleSave}
              disabled={!hasContent || (isSaved && text === existingComment)}
              className={`px-4 py-1.5 text-xs rounded-full font-medium transition-all cursor-pointer ${
                hasContent && !isSaved
                  ? "bg-white/20 hover:bg-white/30 text-white"
                  : isSaved
                    ? "bg-emerald-500/20 text-emerald-300"
                    : "bg-white/5 text-white/30 cursor-not-allowed"
              }`}
              whileHover={hasContent && !isSaved ? { scale: 1.05 } : {}}
              whileTap={hasContent && !isSaved ? { scale: 0.95 } : {}}
            >
              {isSaved ? (
                <span className="flex items-center gap-1.5">
                  <Icon icon="check" />
                  Saved
                </span>
              ) : (
                "Save"
              )}
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
